import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  useTheme,
  Menu,
  MenuItem,
  Avatar,
  ListItemIcon,
  Divider,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Brightness4,
  Brightness7,
  AccountCircle,
  Logout,
  Settings,
  Person,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import uncommonLogo from '../assets/uncommon-logo.png';
import kaizenLogo from '../assets/kaizen-logo.png';

const teamGradients = {
  'UNCOMMON': 'linear-gradient(45deg, #FFB74D 30%, #FFE0B2 90%)',
  'All Teams': 'linear-gradient(45deg, #FFB74D 30%, #FFE0B2 90%)',
  'KAIZEN FINANCIAL': 'linear-gradient(45deg, #FFD700 30%, #FFF64F 90%)',
  'B READY': 'linear-gradient(45deg, #42A5F5 30%, #90CAF9 90%)',
  'HOPE FINANCIAL': 'linear-gradient(45deg, #EC407A 30%, #F48FB1 90%)',
};

const Header = ({ open, toggleDrawer, team = 'UNCOMMON', isMobile, mode, toggleColorMode }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [user, setUser] = useState(null);
  const gradient = teamGradients[team] || teamGradients['UNCOMMON'];

  // Function to refresh user data from localStorage
  const refreshUserData = () => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
        
        // Log profile picture URL for debugging
        if (parsedUser.profile_picture_url) {
          console.log('Profile picture URL loaded:', parsedUser.profile_picture_url);
        } else {
          console.log('No profile picture URL found in user data');
        }
      } catch (error) {
        console.error('Error parsing user data from localStorage:', error);
      }
    } else {
      console.log('No user data found in localStorage');
    }
  };

  // Load user on initial mount
  useEffect(() => {
    refreshUserData();
  }, []);

  // Set up a listener to refresh user data when localStorage changes
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'user') {
        refreshUserData();
      }
    };
    
    // Listen for the custom userDataChanged event
    const handleUserDataChanged = () => {
      refreshUserData();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('userDataChanged', handleUserDataChanged);

    // Check for changes every 2 seconds as a fallback
    // This helps when localStorage is updated in the same window
    const interval = setInterval(refreshUserData, 2000);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('userDataChanged', handleUserDataChanged);
      clearInterval(interval);
    };
  }, []);

  let logo = null;
  if (team === 'UNCOMMON' || team === 'All Teams') {
    logo = uncommonLogo;
  } else if (team === 'KAIZEN FINANCIAL') {
    logo = kaizenLogo;
  }

  // Mobile styles
  const headerHeight = isMobile ? 64 : 64;
  const logoSize = isMobile ? 40 : 48;
  const fontSize = isMobile ? '1.8rem' : '1.5rem';

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleProfile = () => {
    handleClose();
    navigate('/profile');
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        backgroundColor: theme.palette.mode === 'dark' ? 'background.paper' : 'white',
        color: 'text.primary',
        boxShadow: 1,
        width: '100%',
        height: headerHeight,
        justifyContent: 'center',
      }}
    >
      <Toolbar sx={{ minHeight: headerHeight, height: headerHeight }}>
        {!isMobile && (
          <IconButton
            edge="start"
            color="inherit"
            aria-label="open drawer"
            onClick={toggleDrawer}
            sx={{ marginRight: 5 }}
          >
            <MenuIcon />
          </IconButton>
        )}
        <Box
          sx={{
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          {logo ? (
            <Box sx={{ display: 'flex', alignItems: 'center', height: logoSize }}>
              <img
                src={logo}
                alt={`${team} Logo`}
                style={{ height: logoSize, width: 'auto', display: 'block' }}
              />
            </Box>
          ) : (
            <Typography
              variant="h5"
              component="div"
              sx={{
                fontWeight: 600,
                background: gradient,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontSize: fontSize,
              }}
            >
              {team}
            </Typography>
          )}
        </Box>
        <Box sx={{ marginLeft: 'auto' }}>
          <IconButton
            size="large"
            aria-label="account of current user"
            aria-controls="menu-appbar"
            aria-haspopup="true"
            onClick={handleMenu}
            color="inherit"
            sx={{ position: 'relative' }}
          >
            {user?.profile_picture_url ? (
              <Avatar
                src={user.profile_picture_url}
                sx={{ width: 32, height: 32 }}
                alt={user.name || 'User profile'}
              />
            ) : (
              <AccountCircle />
            )}
          </IconButton>
          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleClose}
          >
            <MenuItem onClick={handleProfile}>
              <ListItemIcon>
                <Person fontSize="small" />
              </ListItemIcon>
              Profile
            </MenuItem>
            <Divider />
            <MenuItem onClick={toggleColorMode}>
              <ListItemIcon>
                {mode === 'dark' ? <Brightness7 fontSize="small" /> : <Brightness4 fontSize="small" />}
              </ListItemIcon>
              {mode === 'dark' ? 'Light Mode' : 'Dark Mode'}
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <Logout fontSize="small" />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Header; 